import React, { useEffect } from 'react';
import * as styles from './styles.css';
import Section from '../common/section';
import { useAtomValue } from 'jotai/react';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import atoms from '../../atoms';
import { ISection } from '../../interfaces';
import { Skeleton } from '@mui/material';
import { tracker } from '../../events';
import { useLDClient } from 'launchdarkly-react-client-sdk';

export interface ISectionsProps {
  id?: string;
}

const Sections = ({ id }: ISectionsProps) => {
  const [width, setWidth] = React.useState(window.innerWidth);
  const [show, setShow] = React.useState(false);
  const is_low_res = width < 970;
  const updateWidth = () => setWidth(window.innerWidth);
  React.useEffect(() => {
    window.addEventListener('resize', updateWidth);

    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, [width]);
  const onClick = is_low_res ? () => setShow(!show) : undefined;
  const sections = useAtomValue(atoms.frame.sections) as ISection[];
  const active_section = useAtomValue(atoms.frame.section) as string;
  const active_section_index = sections.findIndex((s) => s.id === active_section);
  const number_of_sections = sections.length;
  const showHideClass = !is_low_res || show ? styles.show : styles.hide;
  const sections_class = `${styles.sections} ${showHideClass}`;
  const progress_description_class = `${styles.progress_description} ${showHideClass}`;
  const view_class = `${styles.view} ${!is_low_res || show ? styles.view_show : styles.view_hide}`;
  const application_id = useAtomValue(atoms.application.application_id);
  const url = window.location.href;
  const ldClient = useLDClient();

  useEffect(() => {
    let isMounted = true;
    let launch_darkly_group = 'undetermined';

    const identifyAndSetGroup = async () => {
      if (application_id && ldClient) {
        await ldClient.identify({ key: application_id, kind: 'user' });
        if (!isMounted) return;
        launch_darkly_group = ldClient.variation('MOESRC-542-control');
      }

      tracker.track({
        eventName: 'Application Section Viewed',
        segmentOptions: { anonymousId: application_id },
        properties: {
          launch_darkly_group,
          application_id,
          url,
        },
      });
    };

    identifyAndSetGroup();

    return () => {
      isMounted = false;
    };
  }, [tracker, application_id, url]);

  return (
    <div id={id} className={view_class} onClick={onClick}>
      <div className={styles.progress_container}>
        <div className={styles.progress}>
          <b>Progress:</b>&nbsp;
          {number_of_sections > 0 ? (
            `Step ${active_section_index + 1} of ${number_of_sections}`
          ) : (
            <Skeleton id="skeleton" data-testid="skeleton" className={styles.progress_skeleton} />
          )}
        </div>

        {is_low_res && <i className={show ? styles.progress_show : styles.progress_hide} />}
      </div>

      <p className={progress_description_class}>
        Your progress saves automatically in this browser so you can pick up where you left off.
      </p>

      <div className={sections_class}>
        {sections.map(({ id, name }, index) => {
          const state =
            index < active_section_index
              ? 'completed'
              : index === active_section_index
                ? 'in_progress'
                : 'incomplete';
          const is_first = index === 0;
          // Only add animation delay if it's not the first section
          const animation_delay = is_first ? '0' : '1.9s';
          return (
            <div key={id} className={styles.section}>
              <div
                style={assignInlineVars({ [styles.animation_delay]: animation_delay })}
                className={styles.icon_wrapper}
              >
                <div className={styles.state[state].icon} />
              </div>
              <Section key={id} name={name} state={state} />
              {!is_first && <div className={styles.state[state].line} />}
            </div>
          );
        })}
      </div>
    </div>
  );
};
export default Sections;
